/*
 * Copyright (c) 2022 CalAmp Corp. All Rights Reserved
 */
import React, {memo, useCallback} from 'react';
import {useDispatch} from 'react-redux';
import {entityId} from '@ion/core/src/services/ConnectUtils';
import {Devices} from '@ion/core/src/types/entities';
import {
  JobDeviceFileTypeChooserPermissionsProps
} from '../../components/jobs/JobTaskDeviceFileTypeChooser';
import {showJobCreateEditor} from '../../components/jobs/state';
import {useJobCreate} from '../../components/jobs/useJobCreate';
import {useCurrentGlobalGroupId} from '../../hooks/useCurrentGlobalGroupId';
import CreateJobEditor, {CreateJobEditorProps} from './CreateJobEditor';
import {JobSearchEngine} from './DeviceActionDialogs';

export interface JobDeviceDialogProps extends
  JobDeviceFileTypeChooserPermissionsProps,
  CreateJobEditorProps,
  JobSearchEngine {
  enableJobDialog?: boolean;
}

/**
 * Holds dialog functionality for maintaining jobs for a device.
 */
export const JobDeviceDialog = ({
  Features,
  filters,
  search,
  areTotalRowsSelected = false,
  totalRowsCount = 0,
  enableJobDialog = false,
  disableAccountChooser = false,
  includeSubAccounts = false,
  jobSearchEngineEnabled = false
}: JobDeviceDialogProps) => {
  const dispatch  = useDispatch();
  const accountId = useCurrentGlobalGroupId();
  const {
    jobCreateSelectedDevices,
    jobCreateSelectedJob,
    jobCreateShow
  } = useJobCreate();

  const onJobClose = useCallback(() => {
    dispatch(showJobCreateEditor());
  }, []);

  if (jobCreateShow && enableJobDialog) {
    const updatedIncludeSubAccounts = updateIncludeSubAccounts(
      includeSubAccounts, jobCreateSelectedDevices, accountId
    );
    return (
      <CreateJobEditor selectedJob={jobCreateSelectedJob}
        selected={jobCreateSelectedDevices}
        areTotalRowsSelected={areTotalRowsSelected}
        totalRowsCount={totalRowsCount}
        Features={Features}
        search={search}
        filters={filters}
        includeSubAccounts={updatedIncludeSubAccounts}
        disableAccountChooser={disableAccountChooser}
        jobSearchEngineEnabled={jobSearchEngineEnabled}
        onCloseDialog={onJobClose} />
    );
  }
};

/**
 * Updates whether job device search criteria should include searching
 * sub-accounts. The original 'includeSubAccounts' prop passed to the
 * JobDeviceDialog component is set according to the 'View Sub-Accounts'
 * toggle value. If that value is false, the value could potentially be
 * updated to true here if there are individually-selected devices (as
 * opposed to devices selected because they match a search criteria) for
 * the job that are not in the current account (such devices could be
 * the result of a global search, for example).
 */
const updateIncludeSubAccounts = (
  includeSubAccounts: boolean,
  selectedDevices: Devices,
  accountId: number
): boolean => {
  const someDevicesNotInAccount = selectedDevices?.some(({account}) => entityId(account) !== accountId);
  return !!(includeSubAccounts || someDevicesNotInAccount);
};

export default memo(JobDeviceDialog);
